import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowSquareOutIcon,
  CaretDownIcon,
  CaretLeftIcon,
  CaretRightIcon,
  FadersIcon,
  GlobeSimpleIcon
} from '@phosphor-icons/react';
import Tooltip from '@/components/ui/Tooltip';
import Button from 'src/components/ui/Button';
import { Toggle } from 'src/components/ui/Toggle';
import { useGroups } from 'src/features/groups/hooks';
import { ChatSearchFilters, FilterAggregations } from '../types';

export interface CategoryGroup {
  key: string;
  label: string;
  categories: string[];
  // Section used to render visual separators between groupings.
  // 'curated' = Regulatory Database + Best Practices & Literature.
  // 'other'   = tenant-specific, Personal Documents, Team Documents.
  section: 'curated' | 'other';
  tooltip?: string;
}

const WELL_KNOWN_CATEGORY_LABELS: Record<string, string> = {
  Guidance: 'Regulatory Database',
  Personal: 'Personal Documents',
  Shared: 'Team Documents'
};

const LITERATURE_CATEGORIES = new Set(['Policies', 'Best Practices', 'International Guidance']);

export function buildCategoryGroups(availableCategories: Set<string>): CategoryGroup[] {
  const groups: CategoryGroup[] = [];

  if (availableCategories.has('Guidance')) {
    groups.push({
      key: 'regulatory',
      label: 'Laws & Guidance',
      categories: ['Guidance'],
      section: 'curated',
      tooltip: 'Laws, regulations, and formal or informal guidance'
    });
  }

  const litCats = [...LITERATURE_CATEGORIES].filter((c) => availableCategories.has(c));
  if (litCats.length > 0) {
    groups.push({
      key: 'literature',
      label: 'Best Practices & Literature',
      categories: litCats,
      section: 'curated',
      tooltip: 'Industry best practices, policies, and international guidance'
    });
  }

  for (const cat of availableCategories) {
    if (cat === 'Guidance' || cat === 'Personal' || cat === 'Shared' || LITERATURE_CATEGORIES.has(cat)) continue;
    const label = WELL_KNOWN_CATEGORY_LABELS[cat] ?? cat;
    groups.push({ key: `tenant_${cat}`, label, categories: [cat], section: 'other' });
  }

  if (availableCategories.has('Personal')) {
    groups.push({ key: 'personal', label: 'Personal Documents', categories: ['Personal'], section: 'other' });
  }
  if (availableCategories.has('Shared')) {
    groups.push({ key: 'team', label: 'Team Documents', categories: ['Shared'], section: 'other' });
  }

  return groups;
}

/**
 * Hook: accumulate every category name observed with a non-zero count across
 * successive aggregation snapshots. Needed because narrowing by
 * domain/sub-domain/document can zero out category counts in filtered
 * aggregations, but we still want those categories visible as toggles.
 */
export function useKnownCategoryNames(aggregations?: FilterAggregations | null): Set<string> {
  const [knownCategoryNames, setKnownCategoryNames] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!aggregations) return;
    const observed = Object.entries(aggregations.category_counts)
      .filter(([, count]) => count > 0)
      .map(([name]) => name);
    if (observed.length === 0) return;

    setKnownCategoryNames((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const name of observed) {
        if (!next.has(name)) {
          next.add(name);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [aggregations]);

  return knownCategoryNames;
}

export const GROUP_SOURCE_PREFIX = 'g-';

export function groupSourceKey(groupId: string): string {
  return `${GROUP_SOURCE_PREFIX}${groupId}`;
}

function withoutGroupSources(sources: string[]): string[] {
  return sources.filter((s) => !s.startsWith(GROUP_SOURCE_PREFIX));
}

export interface SourcesPanelProps {
  /** Current hierarchical filter selection (we read and write `category`). */
  chatFilters: ChatSearchFilters;
  /** Called with the full new category array whenever a group is toggled. */
  onCategoryChange: (newCategories: string[]) => void;
  /** Current Document Hub source selection (library, organisation, etc.). */
  docHubSources?: string[];
  /** Called with the new list of enabled Document Hub sources. */
  onDocHubSourcesChange?: (sources: string[]) => void;
  /**
   * Definitive list of category names available to the current user. When
   * provided (from `useCategoryDefaults`) this is used directly for toggle
   * discovery and is preferred over `aggregations`, which can miss categories
   * when filters narrow their counts to 0.
   */
  availableCategories?: string[] | null;
  /**
   * Latest aggregations response. Used as a fallback for category discovery
   * when `availableCategories` is not provided (e.g. SpecialistsModal).
   */
  aggregations?: FilterAggregations | null;
  /** Optional web access section (chat-only). */
  showWebAccess?: boolean;
  webAccessEnabled?: boolean;
  onToggleWebAccess?: () => void | Promise<void>;
  isUpdatingWebAccess?: boolean;
  /**
   * When true, the Web Access row is shown but non-interactive — used to
   * indicate it's pinned by something outside the user's control (e.g. the
   * selected specialist has pre-approved web search).
   */
  readOnlyWebAccess?: boolean;
  /** Tooltip explaining why Web Access is non-interactive. */
  webAccessReadOnlyHint?: string;
  /** Optional "+ N advanced filters active" hint shown below the Regulatory Wiki section. */
  advancedFiltersActiveCount?: number;
  /** When provided, renders an "All Filters" button below the Regulatory Wiki section. */
  onAllFiltersClick?: () => void;
  /** Whether the All Filters panel is currently open (controls caret direction). */
  allFiltersOpen?: boolean;
  /** Link href for the Regulatory Wiki section header icon (e.g. "/wiki"). */
  wikiHref?: string;
  /** Link href for the Document Hub section header icon (e.g. "/document-hub"). */
  docHubHref?: string;
  /** Optional analytics callback when a group toggle is flipped. */
  onCategoryGroupToggled?: (group: CategoryGroup, enabled: boolean) => void;
  /** Disable all interaction; still renders current selection visually. */
  readOnly?: boolean;
  /**
   * Layout:
   * - `vertical` (default): stacked rows with switch toggles — matches the
   *   chat filter popover's left column.
   * - `horizontal`: wrap-row of pill toggles — more compact when the panel is
   *   used as a top-level section rather than a sidebar column.
   */
  layout?: 'vertical' | 'horizontal';
  className?: string;
}

/** Mapping from wiki category names to Document Hub source keys.
 * Groups whose categories are ALL in this map are rendered as doc-hub toggles. */
const WIKI_CAT_TO_DOC_HUB: Record<string, string> = {
  Personal: 'library',
  Shared: 'organisation'
};

const SourcesPanel: React.FC<SourcesPanelProps> = ({
  chatFilters,
  onCategoryChange,
  docHubSources = [],
  onDocHubSourcesChange,
  availableCategories,
  aggregations,
  showWebAccess = false,
  webAccessEnabled = false,
  onToggleWebAccess,
  isUpdatingWebAccess = false,
  readOnlyWebAccess = false,
  webAccessReadOnlyHint,
  advancedFiltersActiveCount,
  onAllFiltersClick,
  allFiltersOpen = false,
  wikiHref,
  docHubHref,
  onCategoryGroupToggled,
  readOnly = false,
  layout = 'vertical',
  className = ''
}) => {
  const { data: groupsList } = useGroups();
  const membershipGroups = useMemo(
    () => [...(groupsList?.groups ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [groupsList?.groups]
  );

  const [groupsExpanded, setGroupsExpanded] = useState(false);

  const knownFromAggregations = useKnownCategoryNames(availableCategories ? null : aggregations);
  const categoryNames = useMemo(
    () => (availableCategories ? new Set(availableCategories) : knownFromAggregations),
    [availableCategories, knownFromAggregations]
  );
  const categoryGroups = useMemo(() => buildCategoryGroups(categoryNames), [categoryNames]);

  // Wiki groups: exclude Personal/Shared — those are now handled by docHubGroups below.
  const wikiGroups = useMemo(
    () => categoryGroups.filter((g) => !g.categories.every((c) => WIKI_CAT_TO_DOC_HUB[c])),
    [categoryGroups]
  );

  // Doc Hub source toggles are always shown (they control search_document_hub, not wiki search).
  const docHubGroups: { key: string; label: string; source: string; tooltip: string }[] = [
    {
      key: 'dh_organisation',
      label: 'Organisation Folder',
      source: 'organisation',
      tooltip: 'Documents shared across your whole organisation'
    },
    {
      key: 'dh_library',
      label: 'Personal Folder',
      source: 'library',
      tooltip: 'Documents in your Personal Folder, including files uploaded via CompliMind features'
    }
  ];

  const selectedCategories = chatFilters.category || [];
  const categoryGroupStates = useMemo(() => {
    const states: Record<string, boolean> = {};
    for (const group of wikiGroups) {
      states[group.key] = group.categories.some((c) => selectedCategories.includes(c));
    }
    return states;
  }, [selectedCategories, wikiGroups]);

  const handleToggleCategoryGroup = (group: CategoryGroup) => {
    if (readOnly) return;
    const isCurrentlyOn = group.categories.some((c) => selectedCategories.includes(c));
    const newCategories = isCurrentlyOn
      ? selectedCategories.filter((c) => !group.categories.includes(c))
      : [...selectedCategories, ...group.categories];
    onCategoryChange(newCategories);
    onCategoryGroupToggled?.(group, !isCurrentlyOn);
  };

  const groupSourceKeys = useMemo(() => membershipGroups.map((g) => groupSourceKey(g.id)), [membershipGroups]);
  const selectedGroupCount = groupSourceKeys.filter((k) => docHubSources.includes(k)).length;
  const allGroupsOn = groupSourceKeys.length > 0 && selectedGroupCount === groupSourceKeys.length;

  const handleToggleDocHubSource = (source: string) => {
    if (readOnly || !onDocHubSourcesChange) return;
    const isOn = docHubSources.includes(source);
    const next = isOn ? docHubSources.filter((s) => s !== source) : [...docHubSources, source];
    onDocHubSourcesChange(next);
  };

  const handleToggleAllGroups = () => {
    if (readOnly || !onDocHubSourcesChange || groupSourceKeys.length === 0) return;
    if (allGroupsOn) {
      onDocHubSourcesChange(withoutGroupSources(docHubSources));
      return;
    }
    onDocHubSourcesChange([...withoutGroupSources(docHubSources), ...groupSourceKeys]);
  };

  const handleToggleGroup = (groupId: string) => {
    if (readOnly || !onDocHubSourcesChange) return;
    const key = groupSourceKey(groupId);
    const isOn = docHubSources.includes(key);
    onDocHubSourcesChange(isOn ? docHubSources.filter((s) => s !== key) : [...docHubSources, key]);
  };

  const interactionDisabled = readOnly;
  const webAccessInteractionDisabled = interactionDisabled || readOnlyWebAccess;

  const allGroups = [...wikiGroups, ...docHubGroups];

  if (layout === 'horizontal') {
    return (
      <div className={`flex flex-wrap items-center gap-2 ${className}`}>
        {showWebAccess && (
          <button
            type="button"
            onClick={() => {
              if (webAccessInteractionDisabled) return;
              void onToggleWebAccess?.();
            }}
            disabled={webAccessInteractionDisabled || isUpdatingWebAccess}
            title={readOnlyWebAccess ? webAccessReadOnlyHint : undefined}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm transition-colors ${
              webAccessEnabled
                ? 'bg-secondary-blue/10 border-secondary-blue text-secondary-blue font-medium'
                : 'bg-white border-border-default text-content-subtle hover:border-border-active'
            } ${
              webAccessInteractionDisabled || isUpdatingWebAccess
                ? readOnlyWebAccess
                  ? 'cursor-default'
                  : 'opacity-60 cursor-not-allowed'
                : 'cursor-pointer'
            }`}
          >
            <GlobeSimpleIcon size={14} weight="light" />
            Web Access
          </button>
        )}

        {wikiGroups.map((group) => {
          const isOn = categoryGroupStates[group.key];
          return (
            <button
              key={group.key}
              type="button"
              onClick={() => handleToggleCategoryGroup(group)}
              disabled={interactionDisabled}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm transition-colors ${
                isOn
                  ? 'bg-secondary-blue/10 border-secondary-blue text-secondary-blue font-medium'
                  : 'bg-white border-border-default text-content-subtle hover:border-border-active'
              } ${interactionDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
              aria-pressed={isOn}
            >
              {group.label}
            </button>
          );
        })}

        {docHubGroups.map(({ key, label, source, tooltip }) => {
          const isOn = docHubSources.includes(source);
          return (
            <Tooltip key={key} title={tooltip} placement="top">
              <button
                type="button"
                onClick={() => handleToggleDocHubSource(source)}
                disabled={interactionDisabled}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm transition-colors ${
                  isOn
                    ? 'bg-secondary-blue/10 border-secondary-blue text-secondary-blue font-medium'
                    : 'bg-white border-border-default text-content-subtle hover:border-border-active'
                } ${interactionDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                aria-pressed={isOn}
              >
                {label}
              </button>
            </Tooltip>
          );
        })}
        {membershipGroups.length > 0 && (
          <Tooltip title="Documents in group folders you belong to" placement="top">
            <button
              type="button"
              onClick={handleToggleAllGroups}
              disabled={interactionDisabled}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm transition-colors ${
                allGroupsOn
                  ? 'bg-secondary-blue/10 border-secondary-blue text-secondary-blue font-medium'
                  : selectedGroupCount > 0
                    ? 'bg-secondary-blue/10 border-secondary-blue text-secondary-blue'
                    : 'bg-white border-border-default text-content-subtle hover:border-border-active'
              } ${interactionDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
              aria-pressed={allGroupsOn}
            >
              Groups{selectedGroupCount > 0 && !allGroupsOn ? ` (${selectedGroupCount})` : ''}
            </button>
          </Tooltip>
        )}
        {membershipGroups.map((group) => {
          const groupOn = docHubSources.includes(groupSourceKey(group.id));
          return (
            <button
              key={group.id}
              type="button"
              onClick={() => handleToggleGroup(group.id)}
              disabled={interactionDisabled}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm transition-colors ${
                groupOn
                  ? 'bg-secondary-blue/10 border-secondary-blue text-secondary-blue font-medium'
                  : 'bg-white border-border-default text-content-subtle hover:border-border-active'
              } ${interactionDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
              aria-pressed={groupOn}
            >
              {group.name}
            </button>
          );
        })}
      </div>
    );
  }

  const showWebSeparator = showWebAccess && allGroups.length > 0;

  return (
    <div className={`min-w-0 overflow-hidden py-1 ${className}`}>
      {showWebAccess && (
        <Tooltip
          title={readOnlyWebAccess ? webAccessReadOnlyHint || '' : 'When enabled, CompliMind may search the web'}
          placement="right"
        >
          <button
            type="button"
            onClick={() => {
              if (webAccessInteractionDisabled) return;
              void onToggleWebAccess?.();
            }}
            disabled={webAccessInteractionDisabled || isUpdatingWebAccess}
            className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-surface-hover transition-colors ${
              webAccessInteractionDisabled || isUpdatingWebAccess
                ? readOnlyWebAccess
                  ? 'cursor-default'
                  : 'opacity-50 cursor-not-allowed'
                : 'cursor-pointer'
            }`}
          >
            <span
              className={`inline-flex items-center gap-2 text-sm ${
                webAccessEnabled ? 'font-medium text-content-default' : 'text-content-subtle'
              }`}
            >
              <GlobeSimpleIcon size={18} weight="light" className="shrink-0" />
              Web Access
            </span>
            <Toggle checked={webAccessEnabled} size="sm" disabled={webAccessInteractionDisabled} />
          </button>
        </Tooltip>
      )}

      {showWebSeparator && <div className="border-t border-border-default my-1" />}

      {wikiGroups.length > 0 && (
        <div className="px-3 pt-2 pb-1 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wider text-content-subtlest">Regulatory Wiki</span>
          {wikiHref && (
            <Tooltip title="Open Regulatory Wiki" placement="top">
              <a
                href={wikiHref}
                className="text-content-subtlest hover:text-content-subtle transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <ArrowSquareOutIcon size={14} weight="light" />
              </a>
            </Tooltip>
          )}
        </div>
      )}

      {wikiGroups.map((group, idx) => {
        const isOn = categoryGroupStates[group.key];
        const prevGroup = idx > 0 ? wikiGroups[idx - 1] : null;
        const showSectionDivider = prevGroup !== null && prevGroup.section !== group.section;
        return (
          <React.Fragment key={group.key}>
            {showSectionDivider && <div className="border-t border-border-default my-1" />}
            <Tooltip title={group.tooltip ?? ''} placement="right">
              <button
                type="button"
                onClick={() => handleToggleCategoryGroup(group)}
                disabled={interactionDisabled}
                className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-surface-hover transition-colors ${
                  interactionDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                <span
                  className={`min-w-0 truncate text-sm ${isOn ? 'font-medium text-content-default' : 'text-content-subtle'}`}
                >
                  {group.label}
                </span>
                <Toggle checked={isOn} size="sm" disabled={interactionDisabled} />
              </button>
            </Tooltip>
          </React.Fragment>
        );
      })}

      {wikiGroups.length > 0 && (
        <>
          {typeof advancedFiltersActiveCount === 'number' && advancedFiltersActiveCount > 0 && (
            <div className="px-3 py-1">
              <span className="text-xs text-content-subtler">
                + {advancedFiltersActiveCount} advanced filter
                {advancedFiltersActiveCount !== 1 ? 's' : ''} active
              </span>
            </div>
          )}
          {onAllFiltersClick && (
            <Button
              variant="ghost"
              justify="left"
              fullWidth
              className="chat-all-filters-button [&>span]:w-full"
              onClick={onAllFiltersClick}
            >
              <span className="flex w-full items-center justify-between gap-2">
                <span className="inline-flex text-sm items-center gap-2">
                  <FadersIcon size={16} weight="light" />
                  All Wiki Filters
                </span>
                {allFiltersOpen ? (
                  <CaretLeftIcon size={16} className="text-content-subtler shrink-0" />
                ) : (
                  <CaretRightIcon size={16} className="text-content-subtler shrink-0" />
                )}
              </span>
            </Button>
          )}
        </>
      )}

      {docHubGroups.length > 0 && (
        <>
          {wikiGroups.length > 0 && <div className="border-t border-border-default mt-1" />}
          <div className="px-3 pt-3 pb-1 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-content-subtlest">Document Hub</span>
            {docHubHref && (
              <Tooltip title="Open Document Hub" placement="top">
                <a
                  href={docHubHref}
                  className="text-content-subtlest hover:text-content-subtle transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ArrowSquareOutIcon size={14} weight="light" />
                </a>
              </Tooltip>
            )}
          </div>
        </>
      )}

      {docHubGroups.map(({ key, label, source, tooltip }) => {
        const isOn = docHubSources.includes(source);
        return (
          <Tooltip key={key} title={tooltip} placement="right">
            <button
              type="button"
              onClick={() => handleToggleDocHubSource(source)}
              disabled={interactionDisabled}
              className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-surface-hover transition-colors ${
                interactionDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
              }`}
            >
              <span
                className={`min-w-0 truncate text-sm ${isOn ? 'font-medium text-content-default' : 'text-content-subtle'}`}
              >
                {label}
              </span>
              <Toggle checked={isOn} size="sm" disabled={interactionDisabled} />
            </button>
          </Tooltip>
        );
      })}

      {membershipGroups.length > 0 && (
        <>
          <Tooltip title="Documents in group folders you belong to" placement="right">
            <div className="w-full min-w-0 flex items-center gap-1 pr-3">
              <button
                type="button"
                onClick={() => setGroupsExpanded((open) => !open)}
                className="flex-1 min-w-0 flex items-center gap-1.5 px-3 py-1.5 text-left hover:bg-surface-hover transition-colors"
                aria-expanded={groupsExpanded}
              >
                {groupsExpanded ? (
                  <CaretDownIcon size={14} className="text-content-subtlest shrink-0" />
                ) : (
                  <CaretRightIcon size={14} className="text-content-subtlest shrink-0" />
                )}
                <span
                  className={`text-sm truncate ${
                    allGroupsOn || selectedGroupCount > 0 ? 'font-medium text-content-default' : 'text-content-subtle'
                  }`}
                >
                  Groups
                </span>
              </button>
              <Toggle
                checked={allGroupsOn}
                size="sm"
                disabled={interactionDisabled}
                onChange={handleToggleAllGroups}
                aria-label="Toggle all groups"
                className="shrink-0"
              />
            </div>
          </Tooltip>
          {groupsExpanded &&
            membershipGroups.map((group) => {
              const groupOn = docHubSources.includes(groupSourceKey(group.id));
              return (
                <div key={group.id} className="w-full min-w-0 flex items-center gap-1 pl-5 pr-3">
                  <button
                    type="button"
                    onClick={() => handleToggleGroup(group.id)}
                    disabled={interactionDisabled}
                    className={`flex-1 min-w-0 flex items-center py-1.5 text-left hover:bg-surface-hover transition-colors ${
                      interactionDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                    }`}
                  >
                    <Tooltip title={group.name} placement="right">
                      <span
                        className={`min-w-0 truncate text-sm ${groupOn ? 'font-medium text-content-default' : 'text-content-subtle'}`}
                      >
                        {group.name}
                      </span>
                    </Tooltip>
                  </button>
                  <Toggle
                    checked={groupOn}
                    size="sm"
                    disabled={interactionDisabled}
                    onChange={() => handleToggleGroup(group.id)}
                    aria-label={`Toggle ${group.name}`}
                  />
                </div>
              );
            })}
        </>
      )}
    </div>
  );
};

export default SourcesPanel;
